import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import Stepper, { Step as StepContent } from '@/components/onboarding/Stepper'

import { ELEMENT_LIST } from '@/lib/atomElements'
import { ElementSearchDropdown } from '@/components/ElementSearchDropdown'
import { AtomAvatar } from '@/components/AtomAvatar'

const COLORS = [
  '#63b3ed', '#9f7aea', '#68d391', '#fc8181', '#f6ad55', '#76e4f7', '#f687b3', '#ffd700',
]

const ACCESSORIES = ['None', 'glasses', 'hat', 'crown', 'lab', 'headphones', 'star', 'lightning']

const GRADE_OPTIONS = [
  { id: 'middle', icon: '📚', title: 'Middle School', subtitle: 'Grade 6–8' },
  { id: 'high', icon: '🔬', title: 'High School', subtitle: 'Grade 9–10' },
  { id: 'senior', icon: '🧪', title: 'Senior High', subtitle: 'Grade 11–12' },
  { id: 'university', icon: '🎓', title: 'University / Self-learning', subtitle: '' },
]

const EXPERIENCE_OPTIONS = [
  { id: 'beginner', icon: '⚡', title: 'Complete beginner', subtitle: "I've never really studied it" },
  { id: 'basics', icon: '📖', title: 'Know the basics', subtitle: "I've covered some fundamentals" },
  { id: 'intermediate', icon: '🚀', title: 'Intermediate', subtitle: "I'm comfortable with most topics" },
]

type OnboardingStep = 1 | 2 | 3 | 4 | 5 | 6
type AvatarState = { elementN: number; color: string; accessory: string }

export default function SignupPage() {
  const navigate = useNavigate()
  const { refreshProfile } = useAuth()

  const searchParams = new URLSearchParams(window.location.search)
  const requestedStep = parseInt(searchParams.get('step') || '1', 10)
  const initialStep = ([1, 2, 3, 4, 5, 6].includes(requestedStep) ? requestedStep : 1) as OnboardingStep
  const [step, setStep] = useState<OnboardingStep>(initialStep)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Step 2: Grade
  const [selectedGrade, setSelectedGrade] = useState<string | null>(null)

  // Step 3: Experience
  const [selectedExperience, setSelectedExperience] = useState<string | null>(null)

  // Step 4: Account
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Step 5: Avatar
  const [avatarState, setAvatarState] = useState<AvatarState>({
    elementN: 1,
    color: COLORS[5],
    accessory: 'None',
  })

  // Step 6: Username
  const [username, setUsername] = useState('')
  const [usernameStatus, setUsernameStatus] = useState<'checking' | 'available' | 'taken' | 'invalid'>('invalid')
  const [usernameError, setUsernameError] = useState<string | null>(null)

  const currentElement = ELEMENT_LIST.find((e) => e.atomicNumber === avatarState.elementN) || ELEMENT_LIST[0]

  useEffect(() => {
    window.history.replaceState(null, '', `/signup?step=${step}`)
  }, [step])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const stepParam = params.get('step')
    if (!stepParam) return

    const stepNum = Number(stepParam)
    if (![1, 2, 3, 4, 5, 6].includes(stepNum)) return

    setStep(stepNum as OnboardingStep)

    supabase.auth.getSession().then(({ data: { session } }) => {
      const sessionEmail = session?.user?.email
      if (sessionEmail) {
        setEmail(sessionEmail)
      }
    })
  }, [])

  // Check if user has already completed onboarding
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      const { data: { session } } = await supabase.auth.getSession()

      if (session?.user) {
        const { data, error } = await supabase
          .from('profiles')
          .select('username, onboarding_complete')
          .eq('user_id', session.user.id)
          .maybeSingle()

        if (error && error.code === 'PGRST204' && error.message.includes('onboarding_complete')) {
          const { data: fallback } = await supabase
            .from('profiles')
            .select('username')
            .eq('user_id', session.user.id)
            .maybeSingle()

          if (fallback?.username && fallback.username.trim()) {
            navigate('/dashboard', { replace: true })
          }
          return
        }

        // If user has completed onboarding, redirect to dashboard
        const username = data?.username?.trim() ?? ''
        const isOnboarded = (data?.onboarding_complete ?? false) || Boolean(username)
        if (isOnboarded) {
          navigate('/dashboard', { replace: true })
        }
      }
    }

    checkOnboardingStatus()
  }, [navigate])

  // Check username availability (debounced)
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!username) {
        setUsernameStatus('invalid')
        setUsernameError(null)
        return
      }

      const isValid = /^[a-zA-Z0-9_]{3,20}$/.test(username)
      if (!isValid) {
        setUsernameStatus('invalid')
        setUsernameError('Username must be 3-20 characters, letters/numbers/underscores only')
        return
      }

	      setUsernameStatus('checking')
	      setUsernameError(null)

	      const usernameValue = username.trim().toLowerCase()
	      const { data, error } = await supabase
	        .from('profiles')
	        .select('id')
	        .eq('username', usernameValue)
	        .maybeSingle()

	      if (error) {
	        setUsernameStatus('invalid')
	        setUsernameError('Failed to check username')
	        return
	      }

	      const isAvailable = !data
	      setUsernameStatus(isAvailable ? 'available' : 'taken')
	    }, 500)

	    return () => clearTimeout(timer)
	  }, [username])



  const handleGoogleSignIn = async () => {
    setError(null)
    setLoading(true)

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/auth/callback',
      },
    })

    if (error) {
      setError(error.message)
      setLoading(false)
    }
  }

  const canContinue = () => {
    switch (step) {
      case 2:
        return selectedGrade !== null
      case 3:
        return selectedExperience !== null
      case 4:
        return email.length > 0 && password.length >= 8
      case 6:
        return usernameStatus === 'available'
      default:
        return true
    }
  }

  const handleSaveAvatar = async () => {
    setLoading(true)
    setError(null)

    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      setError('Session not established. Please sign in again.')
      setLoading(false)
      return false
    }

    const { error: profileError } = await supabase.from('profiles').upsert({
      user_id: session.user.id,
      avatar_url: JSON.stringify(avatarState),
    }, {
      onConflict: 'user_id',
    })

    if (profileError) {
      setError(profileError.message)
      setLoading(false)
      return false
    }

    await refreshProfile()
    setLoading(false)
    return true
  }

  const handleContinue = async () => {
    setError(null)
    if (step < 6) {
      if (step === 4) {
        await handleCreateAccount()
      } else if (step === 5) {
        const ok = await handleSaveAvatar()
        if (ok) setStep(6)
      } else {
        setStep((step + 1) as OnboardingStep)
      }
    } else {
      await handleFinish()
    }
  }

  const handleBack = () => {
    if (step > 1) {
      setStep((step - 1) as OnboardingStep)
    }
  }

  const handleCreateAccount = async () => {
    setLoading(true)
    setError(null)

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })

    if (error) {
      // If already registered, try signing them in instead
      // They may have started signup before but not finished onboarding
      if (error.message.toLowerCase().includes('already registered') ||
          error.message.toLowerCase().includes('already been registered')) {
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({ email, password })
        if (signInError) {
          setError('This email is already registered. Please sign in instead.')
          setLoading(false)
          return
        }
        // Existing account might have partially completed onboarding already.
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user) {
          const { data: profile, error: profileErr } = await supabase
            .from('profiles')
            .select('username, avatar_url, onboarding_complete')
            .eq('user_id', session.user.id)
            .maybeSingle()

          if (profileErr && profileErr.code === 'PGRST204' && profileErr.message.includes('onboarding_complete')) {
            const { data: fallback } = await supabase
              .from('profiles')
              .select('username, avatar_url')
              .eq('user_id', session.user.id)
              .maybeSingle()
            const isOnboarded = Boolean(fallback?.username?.trim())
            if (isOnboarded) {
              await refreshProfile()
              navigate('/dashboard', { replace: true })
              setLoading(false)
              return
            }
            setStep(fallback?.avatar_url ? 6 : 5)
            setLoading(false)
            return
          }

          const username = profile?.username?.trim() ?? ''
          const isOnboarded = (profile?.onboarding_complete ?? false) || Boolean(username)
          if (isOnboarded) {
            await refreshProfile()
            navigate('/dashboard', { replace: true })
            setLoading(false)
            return
          }
          setStep(profile?.avatar_url ? 6 : 5)
          setLoading(false)
          return
        }

        setStep(5)
        setLoading(false)
        return
      }
      setError(error.message)
      setLoading(false)
      return
    }

    if (!data.user) {
      setError('Signup failed. Please try again.')
      setLoading(false)
      return
    }

    setStep(5)
    setLoading(false)
  }

  const handleFinish = async () => {
    setLoading(true)
    setError(null)

    // Ensure we have an active session before inserting
    const { data: { session } } = await supabase.auth.getSession()

    if (!session) {
      setError('Session not established. Please try signing in.')
      setLoading(false)
      return
    }

    const normalizedUsername = username.trim().toLowerCase()
    const basePayload = {
      user_id: session.user.id,
      username: normalizedUsername,
      display_name: username.trim(),
      avatar_url: JSON.stringify(avatarState),
    }

    let profileError: { message: string; code?: string } | null = null
    const { error } = await supabase.from('profiles').upsert({
      ...basePayload,
      onboarding_complete: true,
    }, {
      onConflict: 'user_id',
    })

    if (error && error.code === 'PGRST204' && error.message.includes('onboarding_complete')) {
      const { error: fallbackError } = await supabase.from('profiles').upsert(basePayload, {
        onConflict: 'user_id',
      })
      if (fallbackError) profileError = fallbackError
    } else if (error) {
      profileError = error
    }

    if (profileError) {
      console.error('Profile upsert error:', JSON.stringify(profileError))
      setError(profileError.message)
      setLoading(false)
      return
    }

    await refreshProfile()
    navigate('/dashboard')
  }

  const getPasswordStrength = (pwd: string) => {
    if (pwd.length < 8) return { label: 'weak', color: 'text-red-400' }
    if (pwd.length < 12) return { label: 'good', color: 'text-yellow-400' }
    if (/[!@#$%^&*]/.test(pwd)) return { label: 'strong', color: 'text-green-400' }
    return { label: 'good', color: 'text-yellow-400' }
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Stepper
        currentStep={step}
        onBack={handleBack}
        onNext={handleContinue}
        canContinue={canContinue()}
        loading={loading}
        nextButtonText={step === 1 ? 'Begin' : 'Continue'}
        completeButtonText="Start learning"
        disableStepIndicators
        aria-label="Scico account setup"
      >
        <StepContent>
          <div className="mx-auto max-w-xl text-center">
            <div className="relative mx-auto mb-6 h-36 w-36 sm:h-44 sm:w-44">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <ellipse cx="50" cy="50" rx="35" ry="12" fill="none" stroke="url(#gradient1)" strokeWidth="0.5" opacity="0.3" transform="rotate(-15 50 50)" />
                <ellipse cx="50" cy="50" rx="30" ry="8" fill="none" stroke="url(#gradient1)" strokeWidth="0.5" opacity="0.3" transform="rotate(30 50 50)" />
                <ellipse cx="50" cy="50" rx="25" ry="6" fill="none" stroke="url(#gradient1)" strokeWidth="0.5" opacity="0.3" transform="rotate(75 50 50)" />
                <circle cx="15" cy="35" r="3" fill="url(#gradient2)" className="animate-pulse" style={{ animationDuration: '2s' }} />
                <circle cx="72" cy="55" r="3" fill="url(#gradient1)" className="animate-pulse" style={{ animationDuration: '2.5s' }} />
                <circle cx="62" cy="25" r="3" fill="url(#gradient1)" className="animate-pulse" style={{ animationDuration: '3s' }} />
                <circle cx="50" cy="50" r="10" fill="url(#gradient2)" />
                <defs>
                  <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#6366f1" />
                  </linearGradient>
                  <radialGradient id="gradient2">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                  </radialGradient>
                </defs>
              </svg>
            </div>

            <h1 className="mb-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Welcome to Scico
            </h1>
            <p className="mb-8 text-base text-slate-400 sm:text-lg">
              Your science journey starts here. We will shape a learning path around your level.
            </p>

            {error && (
              <div role="alert" className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-left text-sm text-red-300">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="mx-auto flex min-h-11 w-full max-w-sm items-center justify-center gap-3 rounded-lg bg-white px-4 py-3 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-300"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.73 1.22 9.24 3.61l6.9-6.9C36.02 2.38 30.37 0 24 0 14.62 0 6.51 5.38 2.56 13.22l8.02 6.23C12.47 13.38 17.77 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.5 24c0-1.6-.14-3.14-.4-4.64H24v9.06h12.66c-.55 2.96-2.22 5.48-4.73 7.18l7.27 5.64C43.2 37.06 46.5 31.04 46.5 24z" />
                <path fill="#FBBC05" d="M10.58 28.55A14.5 14.5 0 0 1 9.5 24c0-1.58.27-3.1.75-4.55l-8.02-6.23A23.95 23.95 0 0 0 0 24c0 3.87.93 7.53 2.56 10.78l8.02-6.23z" />
                <path fill="#34A853" d="M24 48c6.37 0 11.72-2.1 15.62-5.76l-7.27-5.64c-2.03 1.36-4.63 2.16-8.35 2.16-6.23 0-11.53-3.88-13.42-9.21l-8.02 6.23C6.51 42.62 14.62 48 24 48z" />
              </svg>
              Continue with Google
            </button>

            <div className="mx-auto mt-5 flex w-full max-w-sm items-center gap-3" aria-hidden="true">
              <div className="h-px flex-1 bg-slate-700" />
              <span className="text-xs uppercase text-slate-500">or set up with email</span>
              <div className="h-px flex-1 bg-slate-700" />
            </div>
          </div>
        </StepContent>

        <StepContent>
          <div className="mx-auto max-w-2xl">
            <div className="mb-8 text-center">
              <h2 className="mb-2 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                First, tell us about yourself
              </h2>
              <p className="text-slate-400">We'll personalize your learning path</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {GRADE_OPTIONS.map((option) => (
                <button
                  type="button"
                  key={option.id}
                  onClick={() => setSelectedGrade(option.id)}
                  aria-pressed={selectedGrade === option.id}
                  className={`min-h-36 rounded-lg border p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-300 sm:p-5 ${
                    selectedGrade === option.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-700 bg-slate-800/60 hover:bg-slate-800'
                  }`}
                >
                  <div className="mb-3 text-3xl" aria-hidden="true">{option.icon}</div>
                  <div className="mb-1 text-sm font-semibold text-white sm:text-lg">{option.title}</div>
                  <div className="text-sm text-slate-400">{option.subtitle}</div>
                </button>
              ))}
            </div>
          </div>
        </StepContent>

        <StepContent>
          <div className="mx-auto max-w-lg">
            <div className="mb-8 text-center">
              <h2 className="mb-2 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                How comfortable are you with physics?
              </h2>
              <p className="text-slate-400">Choose the closest match. You can change courses later.</p>
            </div>

            <div className="space-y-3">
              {EXPERIENCE_OPTIONS.map((option) => (
                <button
                  type="button"
                  key={option.id}
                  onClick={() => setSelectedExperience(option.id)}
                  aria-pressed={selectedExperience === option.id}
                  className={`min-h-20 w-full rounded-lg border p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-300 sm:p-5 ${
                    selectedExperience === option.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-700 bg-slate-800/60 hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-2xl" aria-hidden="true">{option.icon}</span>
                    <div>
                      <div className="font-semibold text-white sm:text-lg">{option.title}</div>
                      <div className="text-sm text-slate-400">{option.subtitle}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </StepContent>

        <StepContent>
          <div className="mx-auto max-w-md">
            <div className="mb-8 text-center">
              <h2 className="mb-2 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                Almost there!
              </h2>
              <p className="text-slate-400">Create your account to continue</p>
            </div>

            {error && (
              <div role="alert" className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-300">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="min-h-11 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-slate-300">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="min-h-11 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Min. 8 characters"
                />
                {password.length > 0 && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="h-1.5 flex-1 rounded-full bg-slate-700 overflow-hidden">
                      <div
                        className={`h-full ${password.length < 8 ? 'w-1/3 bg-red-400' : password.length < 12 ? 'w-2/3 bg-yellow-400' : 'w-full bg-green-400'}`}
                      />
                    </div>
                    <span className={`text-xs ${getPasswordStrength(password).color}`}>
                      {getPasswordStrength(password).label}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="min-h-11 px-3 text-sm text-slate-400 transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-300"
              >
                Already have an account? Sign in
              </button>
            </div>
          </div>
        </StepContent>

        <StepContent>
          <div className="mx-auto max-w-2xl">
            <div className="mb-6 text-center">
              <h2 className="mb-2 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                Meet your atom
              </h2>
              <p className="text-slate-400">Choose an element and make your science companion yours.</p>
            </div>

            {error && (
              <div role="alert" className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
                {error}
              </div>
            )}

            <div className="mb-5 flex justify-center">
              <AtomAvatar
                state={avatarState}
                size={190}
                className="rounded-lg border border-slate-700 bg-slate-900/50"
              />
            </div>

            <div className="mb-5 text-center">
              <div className="inline-flex flex-wrap items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-800/60 px-4 py-2">
                <span className="text-lg font-bold text-white">{currentElement.symbol}</span>
                <span className="text-slate-500">|</span>
                <span className="text-sm text-slate-400">{currentElement.name}</span>
                <span className="text-slate-500">|</span>
                <span className="text-sm text-slate-400">{currentElement.atomicNumber}</span>
                <span className="text-slate-500">|</span>
                <span className="text-sm text-slate-400">{currentElement.shells.join(', ')}</span>
              </div>
            </div>

            <ElementSearchDropdown
              selectedElementN={avatarState.elementN}
              onSelect={(elementN) => setAvatarState({ ...avatarState, elementN })}
              className="mb-4"
            />

            <div className="mb-5 flex gap-2">
              <button
                type="button"
                onClick={() => setAvatarState({ ...avatarState, elementN: Math.max(1, avatarState.elementN - 1) })}
                aria-label="Previous element"
                className="min-h-11 flex-1 rounded-lg border border-slate-700 py-2 text-lg font-medium text-slate-300 hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-300"
              >
                -
              </button>
              <button
                type="button"
                onClick={() => setAvatarState({ ...avatarState, elementN: Math.min(118, avatarState.elementN + 1) })}
                aria-label="Next element"
                className="min-h-11 flex-1 rounded-lg border border-slate-700 py-2 text-lg font-medium text-slate-300 hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-300"
              >
                +
              </button>
            </div>

            <div className="mb-5">
              <label className="mb-2 block text-sm font-medium text-slate-300">Atom color</label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((color, index) => (
                  <button
                    type="button"
                    key={color}
                    onClick={() => setAvatarState({ ...avatarState, color })}
                    aria-label={`Atom color ${index + 1}`}
                    aria-pressed={avatarState.color === color}
                    className={`h-11 w-11 rounded-full transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-300 ${
                      avatarState.color === color ? 'ring-2 ring-offset-2 ring-offset-slate-950 ring-blue-400' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-300">Accessory</label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {ACCESSORIES.map(acc => {
                  const labels: Record<string, string> = {
                    'None': 'None',
                    'glasses': 'Shades',
                    'hat': 'Cap',
                    'crown': 'Crown',
                    'lab': 'Lab Coat',
                    'headphones': 'Phones',
                    'star': '⭐',
                    'lightning': '⚡'
                  }
                  return (
                    <button
                      type="button"
                      key={acc}
                      onClick={() => setAvatarState({ ...avatarState, accessory: acc })}
                      aria-pressed={avatarState.accessory === acc}
                      className={`min-h-11 rounded-lg border px-2 py-2 text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-300 ${
                        avatarState.accessory === acc
                          ? 'border-indigo-500 bg-indigo-500/10 text-blue-400'
                          : 'border-slate-700 text-slate-400 hover:bg-slate-800'
                      }`}
                    >
                      {labels[acc]}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </StepContent>

        <StepContent>
          <div className="mx-auto max-w-md">
            <div className="mb-8 text-center">
              <h2 className="mb-2 text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                Choose your username
              </h2>
              <p className="text-slate-400">This is how your learning profile will be identified.</p>
            </div>

            {error && (
              <div role="alert" className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
                {error}
              </div>
            )}

            <label htmlFor="username" className="mb-1.5 block text-sm font-medium text-slate-300">
              Username
            </label>
            <div className="relative mb-4">
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                placeholder="Enter username"
                className="min-h-11 w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 pr-12 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {usernameStatus === 'available' && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-400">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              )}
              {usernameStatus === 'taken' && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-red-400">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
              {usernameStatus === 'checking' && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-600 border-t-blue-500" />
                </div>
              )}
            </div>
            {usernameError && (
              <p className="mb-6 text-sm text-red-400">{usernameError}</p>
            )}
            {usernameStatus === 'available' && (
              <p role="status" className="text-sm text-green-400">Username is available.</p>
            )}
          </div>
        </StepContent>
      </Stepper>
    </div>
  )
}
